import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import './Register.css';

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  inviteCode: string;
}

const Register: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState<RegisterFormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    inviteCode: ''
  });
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const validateForm = (): boolean => {
    // Davet kodu kontrolü
    const VALID_INVITE_CODE = 'SOM-RNDM-8X3K'; // Beta davet kodu
    if (formData.inviteCode !== VALID_INVITE_CODE) {
      setError(t('auth.register.errors.invalidInviteCode'));
      return false;
    }

    if (formData.password.length < 6) {
      setError(t('auth.register.errors.passwordTooShort'));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.register.errors.passwordMismatch'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { name, email, password } = formData;
      
      // AuthContext'teki register fonksiyonunu kullan
      await register(name, email, password);

      // Dashboard'a yönlendir
      navigate('/dashboard');
    } catch (err: any) {
      console.error('Register error:', err);
      // Supabase hata mesajlarını işle
      const errorMessage = err.message || err.error_description || t('auth.register.errors.serverError');
      
      // Türkçe hata mesajları
      if (errorMessage.includes('User already registered')) {
        setError(t('auth.register.errors.emailExists'));
      } else if (errorMessage.includes('Password should be at least')) {
        setError('Şifre en az 6 karakter olmalıdır');
      } else if (errorMessage.includes('Unable to validate email')) {
        setError('Geçersiz e-posta adresi');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1>{t('auth.register.title')}</h1>
          <p>{t('auth.register.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="inviteCode">{t('auth.register.inviteCode')}</label>
            <input
              type="text"
              id="inviteCode"
              name="inviteCode"
              value={formData.inviteCode}
              onChange={handleChange}
              placeholder={t('auth.register.inviteCodePlaceholder')}
              required
              disabled={loading}
            />
            <small className="form-hint">{t('auth.register.inviteCodeHint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="name">{t('auth.register.name')}</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('auth.register.namePlaceholder')}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.register.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('auth.register.emailPlaceholder')}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.register.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('auth.register.passwordPlaceholder')}
              required
              minLength={6}
              disabled={loading}
            />
            <small>{t('auth.register.passwordHint')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">{t('auth.register.confirmPassword')}</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t('auth.register.passwordPlaceholder')}
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          <button 
            type="submit" 
            className="register-button"
            disabled={loading}
          >
            {loading ? t('auth.register.registering') : t('auth.register.registerButton')}
          </button>
        </form>

        <div className="register-footer">
          <p>
            {t('auth.register.haveAccount')}{' '}
            <Link to="/login">{t('auth.register.loginLink')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;

